import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ColDef, ICellRendererParams, IRichCellEditorParams } from 'ag-grid-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    activityStateEnum,
    activityTypeEnum,
    getActivityStateLabel,
    getTruckGroupLabel,
    getTruckModeLabel,
    localStorageKeys,
    permissionLevelEnum,
    reportStateEnum,
    reportTypeEnum,
    truckGroupEnum,
} from '../../../../constants';
import { getActivityStateColor } from '../../../../constants/colors';
import { useGetBackPlanningForActivityMutation, useSendPlanningForActivityMutation } from '../../../../hooks/mutations/usePlanningMutation';
import { useAuth } from '../../../../hooks/useAuth';
import { useBoolean } from '../../../../hooks/useBoolean';
import { useTheme } from '../../../../hooks/useTheme';
import { DataItem } from '../../../../services/apiSLTP/dailyReports/activities';
import { ActivityType } from '../../../../services/apiSLTP/dailyReports/activityTypes';
import { createReportIfEmpty } from '../../../../services/apiSLTP/dailyReports/reports';
import { Truck } from '../../../../services/apiSLTP/materials/trucks';
import { User } from '../../../../services/apiSLTP/users';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { formatDate, formatTime } from '../../../../utils/dates';
import SiteCell from '../../../cells/SiteCell';
import StartTimeCell from '../../../cells/StartTimeCell';
import StateCell from '../../../cells/StateCell';
import TrucksPlannedCell from '../../../cells/TrucksPlannedCell';
import DuplicateActivityModal from '../../../modals/operations/planning/DuplicateActivityModal';
import CustomToggle from '../../../ui/CustomToggle';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';
import { useDeleteActivityMutation, useUpdateActivityMutation } from '../../../../hooks/mutations/useActivityMutation';

interface PlanningAmplirollTableProps {
    currentDate: Date;
    activities: DataItem[];
    refetch: () => void;
    isLoading: boolean;
    dataTrucks: Truck[];
    dataActivitiesTypes: ActivityType[];
    dataUsers: User[];
    checkEditableRow: (activity: DataItem) => boolean;
    isUsersLoading: boolean;
    isActivitiesTypesLoading: boolean;
    handleAlignment?: (event: React.MouseEvent<HTMLElement>, newAlignment: number | null) => void;
}

const PlanningAmplirollTable: React.FC<PlanningAmplirollTableProps> = ({
    currentDate,
    activities,
    isLoading,
    refetch,
    dataActivitiesTypes,
    dataTrucks,
    dataUsers,
    checkEditableRow,
    isActivitiesTypesLoading,
    isUsersLoading,
    handleAlignment,
}) => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const { muiTheme } = useTheme();

    const duplicateActivityModal = useBoolean(false);

    const [selectedActivityForDuplicate, setSelectedActivityForDuplicate] = useState<DataItem | null>(null);

    const { mutate: updateActivity } = useUpdateActivityMutation({ onSuccess: () => refetch() });
    const { mutate: deleteActivity } = useDeleteActivityMutation({ onSuccess: () => refetch() });

    const { mutate: sendPlanningForActivity, isLoading: isSendActivityLoading } = useSendPlanningForActivityMutation({ onSettled: () => refetch() });
    const { mutate: getBackPlanningForActivity, isLoading: isGetBackActivityLoading } = useGetBackPlanningForActivityMutation({
        onSettled: () => refetch(),
    });

    const filterAmplirolls = useMemo(() => {
        return (activities || [])
            .filter(
                (activity) =>
                    activity.reportId.userId._id === '66b097d4b0a672302822c715' ||
                    activity.reportId.userId._id === '66e14538c7639ac65e2e6763' ||
                    activity.reportId.truckId?.truckTypeId.group === truckGroupEnum.AMPLIROLL
            )
            .sort((a, b) => {
                const truckA = dataTrucks?.find(
                    (truck) => truck.userId?._id === a.reportId.userId?._id && truck.truckTypeId.group === truckGroupEnum.AMPLIROLL
                );
                const truckB = dataTrucks?.find(
                    (truck) => truck.userId?._id === b.reportId.userId?._id && truck.truckTypeId.group === truckGroupEnum.AMPLIROLL
                );

                if (
                    formatName(a.reportId.userId.firstName, a.reportId.userId.lastName) >
                    formatName(b.reportId.userId.firstName, b.reportId.userId.lastName)
                )
                    return -1;
                if (
                    formatName(a.reportId.userId.firstName, a.reportId.userId.lastName) <
                    formatName(b.reportId.userId.firstName, b.reportId.userId.lastName)
                )
                    return 1;

                const getLowestRank = (activity: DataItem): number => {
                    return (
                        activity.dailyTrucksPlanned
                            .filter((dtp) => dtp.truckTypeId.group === truckGroupEnum.AMPLIROLL)
                            .map((dtp) => dtp.rank ?? Infinity)
                            .sort((r1, r2) => r1 - r2)[0] ?? Infinity
                    );
                };

                const rankA = getLowestRank(a);
                const rankB = getLowestRank(b);

                if (rankA < rankB) return -1;
                if (rankA > rankB) return 1;

                if (a.startTime > b.startTime) return 1;
                if (a.startTime < b.startTime) return -1;
                return 0;
            });
    }, [activities, dataTrucks]);

    const columnDefs = useMemo(
        () =>
            [
                {
                    cellStyle: (params) => {
                        const rowIndex = params.node.rowIndex;
                        if (rowIndex === null) return null;
                        const api = params.api;

                        const prevRowNode = api.getDisplayedRowAtIndex(rowIndex - 1);
                        const nextRowNode = api.getDisplayedRowAtIndex(rowIndex + 1);

                        const prevId = prevRowNode?.data?.reportId._id;
                        const nextId = nextRowNode?.data?.reportId._id;
                        const currentId = params.data?.reportId._id;

                        const marginTop = prevId !== currentId ? '5px' : '0';
                        const marginBottom = nextId !== currentId ? '5px' : '0';

                        const height = `calc(100% - ${parseInt(marginTop) + parseInt(marginBottom)}px)`;

                        return {
                            padding: 0,
                            marginLeft: 5,
                            backgroundColor: params.data?.reportId.userId.color || 'transparent',
                            marginTop,
                            marginBottom,
                            height,
                            borderBottomLeftRadius: nextId !== currentId ? 10 : 0,
                            borderBottomRightRadius: nextId !== currentId ? 10 : 0,
                            borderTopLeftRadius: prevId !== currentId ? 10 : 0,
                            borderTopRightRadius: prevId !== currentId ? 10 : 0,
                            width: 20,
                        };
                    },
                    colId: 'color',
                    resizable: false,
                    filter: false,
                    sortable: false,
                    minWidth: 30,
                    initialWidth: 30,
                    pinned: 'left',
                    valueGetter: (params) => formatName(params.data?.siteId.conducId?.firstName, params.data?.siteId.conducId?.lastName),
                    cellRenderer: () => '',
                    suppressHeaderMenuButton: true,
                },
                {
                    headerName: capitalizeFirstLetter(t('person')),
                    colId: 'name',
                    initialPinned: 'left',
                    initialWidth: 250,
                    field: 'reportId.userId',
                    valueFormatter: (params) => (params.value ? formatName(params.value.firstName, params.value.lastName) : ''),
                    cellEditor: 'agRichSelectCellEditor',

                    cellEditorParams: {
                        values: [...dataUsers]
                            .filter((value) => value.profileId.permsLevel === permissionLevelEnum.CHAUFFEUR)
                            .sort((a, b) => formatName(a.firstName, a.lastName).localeCompare(formatName(b.firstName, b.lastName))),
                        formatValue: (user) => (user ? formatName(user.firstName, user.lastName) : ''),
                        valueListMaxHeight: 500,
                    } as IRichCellEditorParams,

                    keyCreator: (params) => params.value?._id,
                    onCellValueChanged: async (params) => {
                        const newUser = params.newValue;
                        if (!newUser?._id || newUser._id === params.oldValue?._id) return;

                        const response = await createReportIfEmpty({
                            userId: newUser._id,
                            date: params.data.startTime,
                            state: reportStateEnum.PLANNING,
                        });
                        updateActivity({ id: params.data._id, body: { reportId: response.data._id } }, { onSettled: () => refetch() });
                    },
                    editable: (params) => !!params.data && checkEditableRow(params.data),
                    cellRenderer: (params: ICellRendererParams<DataItem>) => {
                        const user = params.value;
                        const activity = params.data;
                        if (!activity) return params.value;

                        const updateActivityIsSpecialHidden = (isSpecialHidden: boolean) => {
                            updateActivity(
                                { id: activity?._id || '', body: { isSpecialHidden: isSpecialHidden } },
                                {
                                    onSettled: () => {
                                        refetch();
                                    },
                                }
                            );
                        };

                        const updateActivityIsHighlight = (isHighlight: boolean) => {
                            updateActivity(
                                { id: activity?._id || '', body: { isHighlight: isHighlight } },
                                {
                                    onSettled: () => {
                                        refetch();
                                    },
                                }
                            );
                        };

                        const formatUserName = (u: any) => formatName(u.firstName, u.lastName);

                        const countDuplicates = activities.filter(
                            (a) => a?.reportId?.userId?._id === user?._id && !a?.reportId?.userId?.isSpecial
                        ).length;

                        const isDuplicate = countDuplicates > 1;

                        return (
                            <div
                                className="fx-r fx-items-c fx-justify-sb w-100 border-radius-10px h-25px pad-l-5px pad-r-5px"
                                style={{
                                    opacity: checkEditableRow(activity) ? undefined : 0.6,
                                    backgroundColor:
                                        (activity?.reportId.userId.isSpecial ? !activity.isSpecialHidden : activity?.isHighlight) &&
                                        activity?.activityState === activityStateEnum.PLANNING
                                            ? activity.reportId.userId.isSpecial
                                                ? muiTheme.palette.info.main
                                                : muiTheme.palette.error.main
                                            : activity?.reportId.reportType === reportTypeEnum.ABSENT
                                              ? muiTheme.palette.error.main
                                              : undefined,
                                }}
                            >
                                <span>{user ? formatUserName(user) : ''}</span>
                                <span className="fx-r fx-items-c">
                                    <div title={capitalizeFirstLetter(t('absent'))}>
                                        {activity?.reportId.reportType === reportTypeEnum.ABSENT && '❗'}
                                    </div>
                                    <div title={capitalizeFirstLetter(t('truckManager'))}>{activity?.isTruckManager && '🎗'}</div>
                                    {isDuplicate ? (
                                        <div className="bg-orange border-radius-10px text-black h-20px w-20px fx-r fx-items-c fx-justify-c">
                                            <div>{countDuplicates}</div>
                                        </div>
                                    ) : null}
                                    {checkEditableRow(activity) &&
                                        activity.reportId.userId.isSpecial &&
                                        activity.activityState === activityStateEnum.PLANNING && (
                                            <div style={{ paddingLeft: 5, paddingRight: 5 }} className="only-over">
                                                <CustomToggle
                                                    checked={activity.isSpecialHidden || false}
                                                    onChange={updateActivityIsSpecialHidden}
                                                    title={t('validate')}
                                                />
                                            </div>
                                        )}
                                    {checkEditableRow(activity) &&
                                        !activity.reportId.userId.isSpecial &&
                                        activity.activityState === activityStateEnum.PLANNING && (
                                            <div style={{ paddingLeft: 5, paddingRight: 5 }} className="only-over">
                                                <CustomToggle
                                                    checked={activity.isHighlight || false}
                                                    onChange={updateActivityIsHighlight}
                                                    title={t('validate')}
                                                />
                                            </div>
                                        )}
                                </span>
                            </div>
                        );
                    },
                },

                {
                    headerName: t('site'),
                    field: 'siteId.name',
                    initialWidth: 300,
                    pinned: 'left',
                    cellRenderer: (params: ICellRendererParams<DataItem>) => {
                        const rowIndex = params.node.rowIndex;
                        if (rowIndex === null) return null;
                        const api = params.api;

                        const prevRowNode = api.getDisplayedRowAtIndex(rowIndex - 1);
                        const nextRowNode = api.getDisplayedRowAtIndex(rowIndex + 1);

                        const prevId = prevRowNode?.data?.siteId._id;
                        const nextId = nextRowNode?.data?.siteId._id;
                        return params.data ? (
                            <SiteCell
                                activity={params.data}
                                currentDate={currentDate}
                                refetch={refetch}
                                disabled={!!user && user?.profileId.permsLevel > permissionLevelEnum.PLANNING}
                                remainingMaterial
                                previousSiteId={prevId}
                                nextSiteId={nextId}
                            />
                        ) : (
                            params.value
                        );
                    },
                    colId: 'site',
                },
                {
                    headerName: capitalizeFirstLetter(t('start')),
                    colId: 'start',
                    initialWidth: 100,
                    valueGetter: (params) => !!params.data && formatTime(new Date(params.data?.startTime)),
                    cellRenderer: (params: ICellRendererParams<DataItem>) => {
                        if (!params.data) return null;
                        return <StartTimeCell activity={params.data} refetch={refetch} disabled={!checkEditableRow(params.data)} />;
                    },
                },

                ...Object.values(truckGroupEnum).map(
                    (group): ColDef<DataItem> => ({
                        headerName: t(getTruckGroupLabel(group)),
                        initialWidth: 350,
                        cellClass: 'ag-cell-remove-of',
                        valueGetter: (params) =>
                            params.data?.dailyTrucksPlanned.map((tp) =>
                                `${tp.trucksAmount} ${tp.truckCompanyId?.designation} ${tp.ridesAmount || ''} ${getTruckModeLabel(tp.mode)} ${tp.truckId?.designation}`.trim()
                            ),
                        cellRenderer: (params: ICellRendererParams<DataItem>) => {
                            if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                            return (
                                <TrucksPlannedCell
                                    activity={params.data}
                                    activityIndex={params.node.rowIndex}
                                    disabled={!checkEditableRow(params.data) || params.data.activityTypeId?.type !== activityTypeEnum.TRUCK}
                                    group={group}
                                    refetch={refetch}
                                    activities={activities || []}
                                    currentDate={currentDate}
                                />
                            );
                        },
                        colId: 'trucks' + group.toString(),
                        filter: 'agTextColumnFilter',
                        enableRowGroup: false,
                        enableValue: false,
                        sortable: false,
                    })
                ),
                {
                    headerName: t('observations'),
                    field: 'observationsPlanning',
                    minWidth: 300,
                    initialFlex: 1,
                    editable: (params) => !!params.data && checkEditableRow(params.data),
                    colId: 'observations',

                    valueFormatter: (params) => params.value || '',

                    valueSetter: (params) => {
                        const newValue = params.newValue?.trim() || '';
                        const oldValue = params.data.observationsPlanning?.trim() || '';
                        if (newValue !== oldValue) {
                            updateActivity(
                                { id: params.data._id, body: { observationsPlanning: newValue } },
                                {
                                    onSettled: () => {
                                        refetch();
                                    },
                                }
                            );
                            params.data.observationsPlanning = newValue;
                        }
                        return true;
                    },

                    cellEditor: 'agLargeTextCellEditor',
                    cellEditorPopup: true,
                    cellStyle: { whiteSpace: 'pre' },
                    filter: 'agTextColumnFilter',
                    enableRowGroup: false,
                    enableValue: false,
                    sortable: false,
                },
                {
                    headerName: capitalizeFirstLetter(t('activity')),
                    field: 'activityTypeId',
                    colId: 'activity',
                    valueFormatter: (params) => (params.value ? params.value.designation : ''),
                    cellEditor: 'agRichSelectCellEditor',
                    cellEditorParams: {
                        values: [...dataActivitiesTypes].sort((a, b) => a.designation.localeCompare(b.designation)),
                        formatValue: (activity) => (activity ? activity.designation : ''),
                        valueListMaxHeight: 500,
                    } as IRichCellEditorParams,
                    keyCreator: (params) => params.value?._id,
                    onCellValueChanged: async (params) => {
                        const newActivity = params.newValue;
                        if (!newActivity?._id || newActivity._id === params.oldValue?._id) return;
                        updateActivity({ id: params.data._id, body: { activityTypeId: newActivity._id } });
                    },
                    editable: (params) => !!params.data && checkEditableRow(params.data),
                },
                {
                    headerName: capitalizeFirstLetter(t('status')),
                    valueGetter: (params) => capitalizeFirstLetter(t(getActivityStateLabel(params.data?.activityState))),
                    cellRenderer: (params: ICellRendererParams<DataItem>) => {
                        if (!params.data) return params.value;
                        return (
                            <StateCell
                                stateColor={getActivityStateColor(params.data?.activityState)}
                                text={t(getActivityStateLabel(params.data?.activityState))}
                            />
                        );
                    },
                    colId: 'status',
                },
                {
                    headerName: capitalizeFirstLetter(t('actions')),
                    initialWidth: 200,
                    colId: 'actions',
                    filter: false,
                    sortable: false,
                    enableRowGroup: false,
                    enableValue: false,
                    cellRenderer: (params: ICellRendererParams<DataItem>) => (
                        <>
                            {!!user && user?.profileId.permsLevel <= permissionLevelEnum.PLANNING && (
                                <IconLine>
                                    {params.data?.activityState === activityStateEnum.PLANNINGSENT &&
                                        params.data?.reportId.reportState !== reportStateEnum.REPORTFILLED && (
                                            <IconButton
                                                iconName="resetTv"
                                                onClick={() => getBackPlanningForActivity({ activityId: params.data?._id || '' })}
                                                title={capitalizeFirstLetter(t('takeBackActivity'))}
                                                loading={isGetBackActivityLoading}
                                            />
                                        )}
                                    {params.data?.activityState === activityStateEnum.PLANNING && (
                                        <IconButton
                                            iconName="verticalAlignTop"
                                            onClick={() => sendPlanningForActivity({ activityId: params.data?._id || '' })}
                                            title={capitalizeFirstLetter(t('sendActivity'))}
                                            loading={isSendActivityLoading}
                                        />
                                    )}
                                    {params.data?.activityState === activityStateEnum.PLANNING && (
                                        <IconButton
                                            iconName="deleteForever"
                                            onClick={() => deleteActivity({ id: params.data?._id || '' })}
                                            title={capitalizeFirstLetter(t('deleteActivity'))}
                                            color="error"
                                        />
                                    )}
                                    <IconButton iconName="contentCopy" onClick={() => duplicateActivityModal.setTrue()} />
                                </IconLine>
                            )}
                        </>
                    ),
                },
                ...((user?.profileId.permsLevel === permissionLevelEnum.ADMIN
                    ? [
                          {
                              headerName: capitalizeFirstLetter(t('_id')),
                              field: '_id',
                              colId: '_id',
                              initialWidth: 250,
                              cellClass: 'usage-col-main',
                              headerClass: 'usage-col-main',
                          },
                      ]
                    : []) as ColDef<DataItem>[]),
            ] as ColDef<DataItem>[],
        [dataActivitiesTypes, dataUsers, dataTrucks, currentDate]
    );

    return (
        <>
            <GenericAgGridTable
                columnDefs={columnDefs}
                rowData={filterAmplirolls}
                loading={isLoading}
                exportCsvFileName={`planning-ampliroll-${formatDate(currentDate)}`}
                agGridProps={{
                    getRowId: ({ data }) => data._id,
                    autoGroupColumnDef: {
                        pinned: 'left',
                    },
                    //domLayout: 'autoHeight',
                    getRowStyle: (params) => {
                        const rowIndex = params.node.rowIndex;
                        if (rowIndex === null) return undefined;

                        const api = params.api;
                        const nextRowNode = api.getDisplayedRowAtIndex(rowIndex + 1);
                        const currentSiteId = params.data?.reportId.userId._id;
                        const nextSiteId = nextRowNode?.data?.reportId.userId._id;

                        if (nextSiteId && currentSiteId !== nextSiteId) {
                            return { borderBottom: '2px solid ' };
                        }

                        return undefined;
                    },
                }}
                storageKey={localStorageKeys.TABLE.OPERATIONS.PLANNING.AMPLIROLL}
                headerRightComponent={
                    <>
                        <ToggleButtonGroup value={1} exclusive onChange={handleAlignment}>
                            <ToggleButton value={0}>{capitalizeFirstLetter(t('planning'))}</ToggleButton>
                            <ToggleButton value={1}>{capitalizeFirstLetter(t('ampliroll'))}</ToggleButton>
                        </ToggleButtonGroup>
                    </>
                }
            />
            {duplicateActivityModal.value && selectedActivityForDuplicate && (
                <DuplicateActivityModal
                    activityId={selectedActivityForDuplicate._id}
                    handleClose={() => duplicateActivityModal.setFalse()}
                    open={duplicateActivityModal.value}
                    name={formatName(selectedActivityForDuplicate.reportId.userId.firstName, selectedActivityForDuplicate.reportId.userId.lastName)}
                />
            )}
        </>
    );
};

export default PlanningAmplirollTable;
